import Axios from 'axios';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { errorCatch } from './errorCatcher.reducer';

export enum CompanyDataProvider {
  CreditSafe = 'CreditSafe'
}

export type SearchedCompanyData = {
  id: string;
  address: string | null;
  companyName: string;
  addressLine1: string | null;
  addressLine2: string | null;
  country: string;
  city: string;
  state: string;
  postalCode: string | null;
  abn: string | null;
  acn: string | null;
  additionalFields: {
    name: string;
    value: string | string[] | null;
  }[];
};

type SearchCompanyDataParams = {
  searchText: string;
  countryIso2: string;
  provider?: CompanyDataProvider;
  afterError?: () => void;
};
export const searchCompanyData = createAsyncThunk(
  'companyData/search',
  async ({ searchText, countryIso2, provider, afterError }: SearchCompanyDataParams, { dispatch, signal }) => {
    const source = Axios.CancelToken.source();
    signal.addEventListener('abort', () => {
      source.cancel('Request cancelled');
    });
    try {
      const { data } = await Axios.get<SearchedCompanyData[]>('/api/v1/signup/search', {
        params: {
          searchText,
          searchProvider: provider || CompanyDataProvider.CreditSafe,
          countryCode: countryIso2
        },
        cancelToken: source.token
      });
      return { data, provider: provider || CompanyDataProvider.CreditSafe };
    } catch (error) {
      if (!Axios.isCancel(error)) {
        afterError && afterError();
        dispatch(errorCatch({ place: 'companyData/search.rejected', data: error }));
      }
      return Promise.reject(error);
    }
  }
);

export type InitState = {
  isLoading: boolean;
  searchedResults: SearchedCompanyData[];
  selectedCompany: SearchedCompanyData | null;
  currentProvider: CompanyDataProvider | null;
};

export const initialState: InitState = {
  isLoading: false,
  searchedResults: [],
  selectedCompany: null,
  currentProvider: null
};

const slice = createSlice({
  name: 'companySearch',
  initialState,
  reducers: {
    setSelectedCompany: (state, action) => {
      state.selectedCompany = action.payload;
    }
  },
  extraReducers: {
    [searchCompanyData.pending.toString()]: state => {
      state.currentProvider = null;
      state.searchedResults = [];
      state.isLoading = true;
    },
    [searchCompanyData.fulfilled.toString()]: (state, action) => {
      const { data, provider } = action.payload;
      state.currentProvider = provider;
      state.searchedResults = data;
      state.isLoading = false;
    },
    [searchCompanyData.rejected.toString()]: state => {
      state.isLoading = false;
    }
  }
});

export const { setSelectedCompany } = slice.actions;
export default slice.reducer;
